import {
  EVENT_END_BEFORE_START_MESSAGE,
  FREE_TICKET_TYPE_PRICE_MESSAGE,
  isEventEndBeforeStart,
  isFreeTicketTypeWithPrice,
} from '@repo/lib';
import { z } from '../zod';
import { AttendeeDetailsApiInputValidationSchema } from '../group-registration/settings';
import { CurrencyCodeSchema } from '../shared/currencyCode';
import { beta } from '../beta';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';
import {
  BaseCheckoutAndEventFieldsInputValidationSchema,
  BasePageFieldsInputValidationSchema,
} from '../page/create';
import { withCustomEmailConfirmationValidation } from '../page/common/notification';
import { CreatePageTaxValidationSchema } from '../page/common/tax';
import { ThemeOverridesToolInputValidationSchema } from '../theme/themeOverrides';
import { EventTicketTypeShowHideLogicInputValidationSchema } from '../ticket-type/showHideLogic';

// Availability behavior options for ticket groups and types
const TicketAvailabilityBehaviorValidationSchema = z
  .enum([
    'always_available',
    'date_time_based',
    'after_ticket_sale_ends',
    'after_ticket_sold_out',
    'after_ticket_ends_or_sold_out',
    'until_event_starts',
  ])
  .openapi({
    description: `Controls when the ticket becomes available for purchase.

| Value | Description | Required fields |
|-------|-------------|-----------------|
| \`always_available\` | Always on sale (default). Sending \`saleStartOn\` or \`saleEndOn\` without a behavior switches it to \`date_time_based\`. | — |
| \`date_time_based\` | On sale between \`saleStartOn\` and \`saleEndOn\`. Omit either date to leave that end open. | \`saleStartOn\` and/or \`saleEndOn\` |
| \`after_ticket_sale_ends\` | Becomes available once the trigger ticket's \`saleEndOn\` is reached. Useful for sequential sale windows (e.g. Early Bird → Standard). | \`triggerTicketType\` or \`triggerTicketGroup\` |
| \`after_ticket_sold_out\` | Becomes available once the trigger ticket sells out. | \`triggerTicketType\` or \`triggerTicketGroup\` |
| \`after_ticket_ends_or_sold_out\` | Becomes available once the trigger ticket's sale ends **or** it sells out, whichever comes first. | \`triggerTicketType\` or \`triggerTicketGroup\` |
| \`until_event_starts\` | On sale until the event's \`startDate\` is reached. | — |`,
    example: 'always_available',
  });

export const TicketFeeDisplayValidationSchema = z.enum(['included', 'separate']).openapi({
  description:
    'How the ticket fees in this group are presented. `included` shows one price with the fee folded in (e.g. "$102", "Includes $3 booking fee"); `separate` shows the price and fee side by side (e.g. "$99 + $3 booking fee"). Fees are stored and reported separately from prices either way. Defaults to `included`.',
  example: 'included',
});

export const TicketTypeInputValidationSchema = z

  .object({
    key: z.string().nullish().openapi({
      description:
        'Temporary string identifier for referencing this ticket type in trigger relationships within the same request. Stripped before saving.',
      example: 'early-bird',
    }),
    name: z.string().min(1).max(200).openapi({
      description: 'Name of the ticket type.',
      example: 'General Admission',
    }),
    description: z.string().nullish().openapi({
      description: 'Description of the ticket type.',
    }),
    status: z.enum(['enabled', 'disabled', 'archived']).nullish().openapi({
      description: 'Status of the ticket type. Defaults to `enabled`.',
      example: 'enabled',
    }),
    reference: z.string().nullish().openapi({
      description: 'External reference ID for the ticket type.',
    }),
    hidden: z.boolean().nullish().openapi({
      description: 'Whether the ticket type is hidden from customers.',
      example: false,
    }),
    hideWhenSoldOut: z.boolean().nullish().openapi({
      description: 'Hide the ticket type when sold out.',
      example: false,
    }),
    hideWhenNotOnSale: z.boolean().nullish().openapi({
      description: 'Hide the ticket type when not on sale.',
      example: false,
    }),
    hideWhenScheduled: z.boolean().nullish().openapi({
      description: 'Hide the ticket type when scheduled for future sale.',
      example: false,
    }),
    hideWhenUnavailable: z.boolean().nullish().openapi({
      description: 'Hide the ticket type when unavailable.',
      example: false,
    }),
    pricing: z.enum(['paid', 'free', 'pwyw']).nullish().openapi({
      description:
        'Pricing type for the ticket. Defaults to `paid`. For `pwyw` (pay what you want), `price` is the minimum amount the customer must pay. A `free` ticket type must have `price` 0 or no price.',
      example: 'paid',
    }),
    price: z.number().min(0).nullish().openapi({
      description:
        'Price in smallest currency unit (cents). Defaults to `0`. For `pwyw` tickets this is the minimum amount. Must be `0` or omitted when `pricing` is `free`.',
      example: 2500,
    }),
    pwywSuggestedPrice: z.number().min(0).nullish().openapi({
      description:
        'Suggested amount in smallest currency unit (cents) shown to customers on `pwyw` tickets. Must be at least `price` (the minimum).',
      example: 5000,
    }),
    discountedFromPrice: z.number().min(0).nullish().openapi({
      description: 'Original price to show as discounted from (strike-through price).',
      example: 3500,
    }),
    feeAmount: z.number().int().positive().nullish().openapi({
      description:
        'A booking fee charged once per ticket of this type, on top of `price`, in the smallest currency unit of the event currency. Customers see it under the name set by the page theme label `bookingFeeText` ("Booking fee" by default), shown the way the ticket group\'s `feeDisplay` says. Pass `null` to remove an existing fee.',
      example: 300,
    }),

    capacity: z.number().min(0).nullish().openapi({
      description:
        'Maximum number of tickets available. Omit or null for unlimited. `0` marks the ticket type as sold out.',
    }),
    minQuantity: z.number().min(0).nullish().openapi({
      description: 'Minimum tickets required per order.',
      example: 1,
    }),
    maxQuantity: z.number().min(1).nullish().openapi({
      description: 'Maximum tickets per order.',
      example: 10,
    }),
    showAvailableQuantity: z.boolean().nullish().openapi({
      description: 'Show the number of available tickets to customers.',
      example: true,
    }),
    showTicketSaleDates: z.boolean().nullish().openapi({
      description: 'Show sale start/end dates to customers.',
      example: true,
    }),
    imageId: ObjectIdStringValidationSchema.nullish().openapi({
      description: 'File ID for the ticket type image. Must be an uploaded file ID.',
      example: '507f1f77bcf86cd799439011',
    }),
    availabilityBehavior: TicketAvailabilityBehaviorValidationSchema.nullish(),
    saleStartOn: z.iso.datetime().nullish().openapi({
      description:
        'Sale start date in ISO 8601 format. Used with `availabilityBehavior: "date_time_based"` to set when sales open. Omit to leave the start unbounded. For `after_ticket_sale_ends` and `after_ticket_ends_or_sold_out` behaviors, this is automatically derived from the trigger ticket type\'s `saleEndOn` (or the event `startDate` if the trigger uses `until_event_starts`) when the trigger is referenced by `key` within the same request. Setting a sale date on an always-available ticket type, without sending `availabilityBehavior`, switches it to `date_time_based`.',
      example: '2025-06-01T09:00:00Z',
    }),
    saleEndOn: z.iso.datetime().nullish().openapi({
      description:
        'Sale end date in ISO 8601 format. Used with `availabilityBehavior: "date_time_based"` to set when sales close. When set, any ticket type with `availabilityBehavior: "after_ticket_sale_ends"` that references this ticket type via `triggerTicketType` will automatically become available at this time. Setting a sale date on an always-available ticket type, without sending `availabilityBehavior`, switches it to `date_time_based`.',
      example: '2025-06-14T23:59:59Z',
    }),
    triggerTicketTypeId: z.string().nullish().openapi({
      description:
        "The `key` or ID of the ticket type that triggers this ticket type to become available. Use a `key` value to reference another ticket type in the same request. Required when `availabilityBehavior` is `after_ticket_sale_ends`, `after_ticket_sold_out`, or `after_ticket_ends_or_sold_out`. When referenced by `key`, `saleStartOn` is automatically derived from the trigger's `saleEndOn` for `after_ticket_sale_ends` and `after_ticket_ends_or_sold_out` behaviors.",
      example: 'early-bird',
    }),
    showHideLogic: EventTicketTypeShowHideLogicInputValidationSchema.nullish(),
  })
  .openapi('TicketTypeInput');

// Refines a copy rather than TicketTypeInputValidationSchema itself: the ticket type
// create/update schemas derive from it with .omit()/.partial(), which throw on refinements.
export function withFreeTicketTypePriceCheck<
  T extends z.ZodType<{ pricing?: string | null; price?: number | null }>,
>(schema: T): T {
  return schema.refine(ticketType => !isFreeTicketTypeWithPrice(ticketType), {
    message: FREE_TICKET_TYPE_PRICE_MESSAGE,
    path: ['price'],
  });
}

// Layout schema for ticket groups
const TicketGroupLayoutInputValidationSchema = z
  .object({
    type: z.enum(['list', 'grid', 'accordion']).nullish().openapi({
      description: 'Layout type for displaying ticket types.',
      example: 'list',
    }),
    columns: z.number().min(1).max(4).nullish().openapi({
      description: 'Number of columns (for grid layout).',
      example: 2,
    }),
    collapse: z.boolean().nullish().openapi({
      description: 'Whether the group is collapsed by default (for accordion layout).',
      example: false,
    }),
    alignment: z.enum(['default', 'center', 'stack']).nullish().openapi({
      description: 'Content alignment within the layout.',
      example: 'default',
    }),
    spacing: z.enum(['comfortable', 'compact']).nullish().openapi({
      description: 'Spacing between ticket types.',
      example: 'comfortable',
    }),
    imageSize: z.enum(['small', 'medium', 'large', '']).nullish().openapi({
      description: 'Size of ticket type images.',
      example: 'small',
    }),
    showTicketGroupName: z.boolean().nullish().openapi({
      description: 'Show the ticket group name.',
      example: true,
    }),
    showTicketTypeName: z.boolean().nullish().openapi({
      description: 'Show ticket type names.',
      example: true,
    }),
    showTicketPrice: z.boolean().nullish().openapi({
      description: 'Show ticket prices.',
      example: true,
    }),
    showTicketTotalPrice: z.boolean().nullish().openapi({
      description: 'Show total price for selected quantity.',
      example: true,
    }),
    showPwywPlaceholder: z.boolean().nullish().openapi({
      description: 'Show the amount placeholder in the pay-what-you-want ticket input.',
      example: true,
    }),
    showTicketFee: z.boolean().nullish().openapi({
      description: 'Show the booking fee under each ticket price.',
      example: true,
    }),
    showTicketSaleDateTimezone: z.boolean().nullish().openapi({
      description: 'Show sale date timezone.',
      example: true,
    }),
  })
  .openapi('TicketGroupLayoutInput');

// Preselect schema for ticket groups
const TicketGroupPreselectInputValidationSchema = z
  .object({
    enabled: z.boolean().nullish().openapi({
      description: 'Whether preselection is enabled.',
      example: false,
    }),
    ticketTypeId: ObjectIdStringValidationSchema.nullish().openapi({
      description: 'ID of the ticket type to preselect.',
    }),
    quantity: z.number().min(1).nullish().openapi({
      description: 'Quantity to preselect.',
      example: 1,
    }),
  })
  .openapi('TicketGroupPreselectInput');

const BulkDiscountInputValidationSchema = z
  .object({
    minQuantity: z.number().min(1).openapi({
      description: 'Minimum quantity to qualify for this discount tier.',
      example: 5,
    }),
    maxQuantity: z.number().min(1).openapi({
      description: 'Maximum quantity for this discount tier.',
      example: 10,
    }),
    percentOff: z.number().min(0).max(100).openapi({
      description: 'Percentage discount for this tier.',
      example: 10,
    }),
  })
  .openapi('BulkDiscountInput');

export const TicketGroupInputValidationSchema = z
  .object({
    key: z.string().nullish().openapi({
      description:
        'Temporary string identifier for referencing this ticket group in trigger relationships within the same request. Stripped before saving.',
      example: 'general-admission',
    }),
    name: z.string().min(1).max(200).openapi({
      description: 'Name of the ticket group.',
      example: 'General Admission',
    }),
    description: z.string().nullish().openapi({
      description: 'Description of the ticket group.',
    }),
    status: z.enum(['enabled', 'disabled', 'archived']).nullish().openapi({
      description: 'Status of the ticket group. Defaults to `enabled`.',
      example: 'enabled',
    }),
    reference: z.string().nullish().openapi({
      description: 'External reference ID for the ticket group.',
    }),
    layout: TicketGroupLayoutInputValidationSchema.nullish().openapi({
      description: 'Layout configuration for displaying ticket types.',
    }),
    ticketSelectionType: z.enum(['single', 'multiple', 'quantity']).nullish().openapi({
      description: 'How customers can select tickets. Defaults to `quantity`.',
      example: 'quantity',
    }),
    feeDisplay: TicketFeeDisplayValidationSchema.nullish(),
    preselect: TicketGroupPreselectInputValidationSchema.nullish().openapi({
      description: 'Preselection configuration.',
    }),
    capacity: z.number().min(0).nullish().openapi({
      description:
        'Maximum tickets that can be sold across all ticket types in this group. Omit or null for unlimited. `0` marks the ticket group as sold out.',
    }),
    hidden: z.boolean().nullish().openapi({
      description: 'Whether the ticket group is hidden from customers.',
      example: false,
    }),
    hideWhenSoldOut: z.boolean().nullish().openapi({
      description: 'Hide the ticket group when all tickets are sold out.',
      example: false,
    }),
    hideWhenNotOnSale: z.boolean().nullish().openapi({
      description: 'Hide the ticket group when not on sale.',
      example: false,
    }),
    hideWhenScheduled: z.boolean().nullish().openapi({
      description: 'Hide the ticket group when scheduled for future sale.',
      example: false,
    }),
    hideWhenUnavailable: z.boolean().nullish().openapi({
      description: 'Hide the ticket group when unavailable.',
      example: false,
    }),
    bulkDiscounts: z.array(BulkDiscountInputValidationSchema).nullish().openapi({
      description: 'Bulk discount tiers for quantity-based discounts.',
    }),
    availabilityBehavior: TicketAvailabilityBehaviorValidationSchema.nullish(),
    saleStartOn: z.iso.datetime().nullish().openapi({
      description:
        'Sale start date in ISO 8601 format. Used with `availabilityBehavior: "date_time_based"` to set when sales open. Omit to leave the start unbounded. For `after_ticket_sale_ends` and `after_ticket_ends_or_sold_out` behaviors, this is automatically derived from the trigger ticket group\'s `saleEndOn` (or the event `startDate` if the trigger uses `until_event_starts`) when the trigger is referenced by `key` within the same request. Setting a sale date on an always-available ticket group, without sending `availabilityBehavior`, switches it to `date_time_based`.',
      example: '2025-06-01T09:00:00Z',
    }),
    saleEndOn: z.iso.datetime().nullish().openapi({
      description:
        'Sale end date in ISO 8601 format. Used with `availabilityBehavior: "date_time_based"` to set when sales close. When set, any ticket group with `availabilityBehavior: "after_ticket_sale_ends"` that references this group via `triggerTicketGroup` will automatically become available at this time. Setting a sale date on an always-available ticket group, without sending `availabilityBehavior`, switches it to `date_time_based`.',
      example: '2025-06-14T23:59:59Z',
    }),
    triggerTicketGroupId: z.string().nullish().openapi({
      description:
        "The `key` or ID of the ticket group that triggers this ticket group to become available. Use a `key` value to reference another ticket group in the same request. Required when `availabilityBehavior` is `after_ticket_sale_ends`, `after_ticket_sold_out`, or `after_ticket_ends_or_sold_out`. When referenced by `key`, `saleStartOn` is automatically derived from the trigger's `saleEndOn` for `after_ticket_sale_ends` and `after_ticket_ends_or_sold_out` behaviors.",
      example: 'general-admission',
    }),
    ticketTypes: z
      .array(withFreeTicketTypePriceCheck(TicketTypeInputValidationSchema))
      .min(1)
      .openapi({
        description: 'Ticket types within this group.',
      }),
  })
  .openapi('TicketGroupInput');

const EventDetailsDiscountInputValidationSchema = z.object({
  quantityCondition: z.enum(['total_quantity', 'ticket_type_quantity']).openapi({
    description:
      'Whether the discount applies to the total order quantity or to specific ticket types. ' +
      '`total_quantity` fires based on the total number of tickets in the order. ' +
      '`ticket_type_quantity` fires only when specific ticket types reach the threshold — assign a `key` to each relevant ticket type in `ticketGroups[].ticketTypes[].key` and reference those keys in `ticketTypeIds[].key` within the same request. Keys are resolved to MongoDB ObjectIds at save time and do not appear in the response.',
    example: 'total_quantity',
  }),
  ticketTypeIds: z
    .array(z.union([z.object({ key: z.string() }), z.object({ id: z.string() })]))
    .nullish()
    .openapi({
      description:
        'Ticket types this discount applies to. Only used when `quantityCondition` is `ticket_type_quantity`. ' +
        'Use `{ key }` to reference a ticket type being created in the same request (the `key` must match `ticketGroups[].ticketTypes[].key`). ' +
        'Use `{ id }` to reference an existing ticket type by its ObjectId (useful when updating an event).',
    }),
  minQuantity: z.number().min(1).openapi({
    description: 'Minimum quantity required for this discount to apply.',
    example: 5,
  }),
  maxQuantity: z.number().min(1).nullish().openapi({
    description: 'Maximum quantity for this discount tier. Omit for no upper limit.',
    example: 10,
  }),
  percentOff: z.number().min(0).max(100).nullish().openapi({
    description: 'Percentage discount. Cannot be combined with `amountOff`.',
    example: 10,
  }),
  amountOff: z.number().min(0).nullish().openapi({
    description:
      'Fixed discount in smallest currency unit (e.g. cents). Cannot be combined with `percentOff`.',
    example: 500,
  }),
});

const UpdateEventDetailsDiscountInputValidationSchema = z.object({
  quantityCondition: z.enum(['total_quantity', 'ticket_type_quantity']).openapi({
    description:
      'Whether the discount applies to the total order quantity or to specific ticket types. ' +
      '`total_quantity` fires based on the total number of tickets in the order. ' +
      '`ticket_type_quantity` fires only when specific ticket types reach the threshold.',
    example: 'total_quantity',
  }),
  ticketTypeIds: z
    .array(z.object({ id: ObjectIdStringValidationSchema }))
    .nullish()
    .openapi({
      description:
        'Ticket types this discount applies to. Only used when `quantityCondition` is `ticket_type_quantity`. ' +
        'Use `{ id }` to reference an existing ticket type by its ObjectId when updating an event.',
    }),
  minQuantity: z.number().min(1).openapi({
    description: 'Minimum quantity required for this discount to apply.',
    example: 5,
  }),
  maxQuantity: z.number().min(1).nullish().openapi({
    description: 'Maximum quantity for this discount tier. Omit for no upper limit.',
    example: 10,
  }),
  percentOff: z.number().min(0).max(100).nullish().openapi({
    description: 'Percentage discount. Cannot be combined with `amountOff`.',
    example: 10,
  }),
  amountOff: z.number().min(0).nullish().openapi({
    description:
      'Fixed discount in smallest currency unit (e.g. cents). Cannot be combined with `percentOff`.',
    example: 500,
  }),
});

const EventDetailsBaseInputValidationSchema = z.object({
  type: z.enum(['in_person', 'virtual', 'hybrid']).nullish().openapi({
    description: 'Type of event. Defaults to `in_person`.',
    example: 'in_person',
  }),
  currency: CurrencyCodeSchema.nullish().openapi({
    description: 'Currency code (lowercase). Defaults to `usd`.',
    example: 'usd',
  }),
  startDate: z.iso.datetime().nullish().openapi({
    description: 'Event start date in ISO 8601 format.',
    example: '2025-06-15T09:00:00Z',
  }),
  endDate: z.iso.datetime().nullish().openapi({
    description: 'Event end date in ISO 8601 format. Must be on or after `startDate`.',
    example: '2025-06-15T17:00:00Z',
  }),
  timezone: z.string().nullish().openapi({
    description: 'Event timezone.',
    example: 'America/New_York',
  }),
  location: z.string().nullish().openapi({
    description: 'Event location address. Required for `in_person` and `hybrid` events.',
  }),
  meetingLink: z
    .string()
    .regex(/^https?:\/\/.+/, 'Must be a valid URL')
    .nullish()
    .openapi({
      description:
        'Virtual meeting link (e.g., Zoom, Teams). Required for `virtual` and `hybrid` events.',
      example: 'https://zoom.us/j/123456789',
    }),
  capacity: z.number().min(0).nullish().openapi({
    description: 'Total event capacity. Omit for unlimited. `0` marks the event as sold out.',
  }),
  mapsLink: z.string().nullish().openapi({
    description: 'Google Maps or other map URL for the event location.',
    example: 'https://maps.google.com/?q=...',
  }),
  platform: z
    .enum([
      'zoom',
      'google_meet',
      'microsoft_teams',
      'youtube',
      'hopin',
      'vimeo',
      'discord',
      'other',
    ])
    .nullish()
    .openapi({
      description: 'Virtual meeting platform. Applies to `virtual` and `hybrid` events.',
      example: 'zoom',
    }),
  taxBehavior: z.enum(['inclusive', 'exclusive', 'automatic', '']).nullish().openapi({
    description: 'Tax behaviour for this event. Defaults to the store setting when omitted.',
    example: 'exclusive',
  }),
  taxCode: z.string().nullish().openapi({
    description: 'Stripe tax code for this event.',
    example: 'txcd_10000000',
  }),
  fixedTaxRateIds: z.array(ObjectIdStringValidationSchema).max(1).nullish().openapi({
    description:
      'List of Tax Rate IDs applied to this event. When tax.mode is "fixed" and this is omitted on create, the seller default rate is applied.',
  }),
  pdfTicketsEnabled: z.boolean().nullish().openapi({
    description: 'Whether PDF tickets are generated and sent to attendees after purchase.',
    example: true,
  }),
  limitRegistrations: z
    .object({
      enabled: z.boolean().nullish().openapi({
        description:
          'Prevent a guest who has already registered for or bought tickets to this event from registering again. Defaults to `false`.',
      }),
    })
    .nullish()
    .openapi({
      description: 'Duplicate-registration prevention settings for this event.',
    }),
});

const endsOnOrAfterStart = (eventDetails: { startDate?: string | null; endDate?: string | null }) =>
  !isEventEndBeforeStart(eventDetails.startDate, eventDetails.endDate);

const END_DATE_ORDER_ISSUE = { message: EVENT_END_BEFORE_START_MESSAGE, path: ['endDate'] };

export const EventDetailsInputValidationSchema = EventDetailsBaseInputValidationSchema.extend({
  discounts: z
    .array(EventDetailsDiscountInputValidationSchema)
    .nullish()
    .openapi({
      description:
        'Bulk discounts applied when a customer purchases a minimum number of tickets. ' +
        'To apply a discount to specific ticket types, assign a `key` to each relevant ticket type in `ticketGroups[].ticketTypes[].key` and reference those keys in `discounts[].ticketTypeIds[].key` within the same request.',
      example: [
        {
          quantityCondition: 'ticket_type_quantity',
          ticketTypeIds: [{ key: 'vip-ticket' }],
          minQuantity: 5,
          percentOff: 10,
        },
      ],
    }),
  attendeeDetails: beta(AttendeeDetailsApiInputValidationSchema.optional()),
})
  .refine(
    data => {
      if (data.type === 'in_person') {
        return !data.meetingLink;
      }
      return true;
    },
    {
      message: "In-person events cannot have a 'meetingLink'.",
      path: ['type'],
    },
  )
  .refine(
    data => {
      if (data.type === 'virtual') {
        return !data.location;
      }
      return true;
    },
    {
      message: "Virtual events cannot have 'location'.",
      path: ['type'],
    },
  )
  .refine(endsOnOrAfterStart, END_DATE_ORDER_ISSUE);

export const UpdateEventDetailsInputValidationSchema = EventDetailsBaseInputValidationSchema.extend(
  {
    discounts: z
      .array(UpdateEventDetailsDiscountInputValidationSchema)
      .nullish()
      .openapi({
        description:
          'Bulk discounts applied when a customer purchases a minimum number of tickets. ' +
          'When updating an event, ticket-specific discounts must reference existing ticket types by `{ id }`.',
        example: [
          {
            quantityCondition: 'ticket_type_quantity',
            ticketTypeIds: [{ id: '507f1f77bcf86cd799439011' }],
            minQuantity: 5,
            percentOff: 10,
          },
        ],
      }),
    attendeeDetails: beta(
      AttendeeDetailsApiInputValidationSchema.optional().meta({
        description:
          'Attendee details for this event. Only the settings you send change; the rest keep their current values.',
      }),
    ),
  },
)
  .refine(
    data => {
      if (data.type === 'in_person') {
        return !data.meetingLink;
      }
      return true;
    },
    {
      message: "In-person events cannot have a 'meetingLink'.",
      path: ['type'],
    },
  )
  .refine(
    data => {
      if (data.type === 'virtual') {
        return !data.location;
      }
      return true;
    },
    {
      message: "Virtual events cannot have 'location'.",
      path: ['type'],
    },
  )
  .refine(endsOnOrAfterStart, END_DATE_ORDER_ISSUE);

export const CreateEventPageBaseInputValidationSchema = BasePageFieldsInputValidationSchema.extend(
  BaseCheckoutAndEventFieldsInputValidationSchema.shape,
)
  .extend({
    title: z.string().max(500).optional().openapi({
      description: 'Public title shown to customers on the page. Defaults to `name` when omitted.',
      example: 'Get The Premium Course Today',
    }),
    description: z.string().nullish().openapi({
      description: 'Page description shown to customers. Accepts HTML or plain text.',
      example: '<p>Welcome to our <strong>premium</strong> course!</p>',
    }),
    tax: CreatePageTaxValidationSchema.nullish(),
    eventDetails: EventDetailsInputValidationSchema.nullish().openapi({
      description: 'Event-specific details like type, dates, location.',
    }),
    ticketGroups: z.array(TicketGroupInputValidationSchema).nullish().openapi({
      description:
        'Ticket groups with their ticket types. If not provided, a default ticket group with a free ticket will be created.',
    }),
    showConfirmationEventName: z.boolean().nullish().openapi({
      description: 'Show the event name on payment confirmation page. Defaults to `true`.',
      example: true,
    }),
    showConfirmationEventDateTime: z.boolean().nullish().openapi({
      description: 'Show the event date on payment confirmation page. Defaults to `true`.',
      example: true,
    }),
    showConfirmationEventLocation: z.boolean().nullish().openapi({
      description: 'Show the event location on payment confirmation page. Defaults to `true`.',
      example: true,
    }),
    showConfirmationEventMeetingLink: z.boolean().nullish().openapi({
      description:
        'Show the event virtual meeting link on payment confirmation page. Defaults to `true`.',
      example: true,
    }),
    showConfirmationEventTickets: z.boolean().nullish().openapi({
      description: 'Show the purchased tickets on payment confirmation page. Defaults to `true`.',
      example: true,
    }),
    showConfirmationAddToCalendar: z.boolean().nullish().openapi({
      description:
        'Show the add to calendar button on payment confirmation page. Defaults to `true`.',
      example: true,
    }),
    showConfirmationEventAddOns: z.boolean().nullish().openapi({
      description: 'Show the purchased add-ons on payment confirmation page. Defaults to `true`.',
      example: true,
    }),
    imageIds: z.array(ObjectIdStringValidationSchema).nullish().openapi({
      description:
        'Array of images for the page gallery. All referenced file IDs must exist and belong to your account.',
    }),
  })
  .openapi('CreateEventPageBaseInput');

export const CreateEventPageInputValidationSchema = withCustomEmailConfirmationValidation(
  CreateEventPageBaseInputValidationSchema,
).openapi('CreateEventPageInput');

// MCP callers cannot set `status` — every page they create goes live immediately.
// `.omit()` throws on a schema that already carries refinements, so it has to run
// against the base schema with the refinement re-applied on top.
// MCP advertises themeOverrides loose; see ThemeOverridesToolInputValidationSchema.
export const CreateEventPageInputWithoutStatusValidationSchema =
  withCustomEmailConfirmationValidation(
    CreateEventPageBaseInputValidationSchema.omit({ status: true }).extend({
      themeOverrides: ThemeOverridesToolInputValidationSchema,
    }),
  );

export type CreateEventPageInput = z.infer<typeof CreateEventPageInputValidationSchema>;
